import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import repositoryContainer from '../../../services/RepositoryContainer';

const CopyScheduleModal = ({ isOpen, onClose, onCopy, isCopying }) => {
  const [availabilityData, setAvailabilityData] = useState({});
  const [selectedDateSlotIds, setSelectedDateSlotIds] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentMonth, setCurrentMonth] = useState(new Date());

  useEffect(() => {
    if (isOpen) {
      fetchAvailabilityData();
    }
  }, [isOpen, currentMonth]);

  const fetchAvailabilityData = async () => {
    setIsLoading(true);
    try {
      const now = currentMonth;
      const start = new Date(now.getFullYear(), now.getMonth(), 1)
        .toISOString()
        .split('T')[0];
      const end = new Date(now.getFullYear(), now.getMonth() + 1, 0)
        .toISOString()
        .split('T')[0];

      const response = await repositoryContainer.dateSlot.getAdminCalendar(start, end);
      setAvailabilityData(response.dates || {});
    } catch (error) {
      console.error('Failed to fetch availability:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getDaysInMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  };

  const handleDateSelect = (dateSlotId) => {
    setSelectedDateSlotIds(prev =>
      prev.includes(dateSlotId)
        ? prev.filter(id => id !== dateSlotId)
        : [...prev, dateSlotId]
    );
  };

  const handleCopy = () => {
    if (selectedDateSlotIds.length === 0) {
      alert('Please select at least one date');
      return;
    }
    onCopy(selectedDateSlotIds);
  };

  const handlePrevMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1));
  };

  const handleNextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1));
  };

  if (!isOpen) return null;

  const daysInMonth = getDaysInMonth(currentMonth);
  const firstDay = getFirstDayOfMonth(currentMonth);
  const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  const calendarDays = [];
  for (let i = 0; i < firstDay; i++) {
    calendarDays.push(null);
  }
  for (let i = 1; i <= daysInMonth; i++) {
    calendarDays.push(i);
  }

  const monthName = currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-background/80 backdrop-blur-sm">
      <div className="bg-card border border-border rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Modal Header */}
        <div className="flex items-center justify-between p-4 md:p-6 border-b border-border">
          <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground">
            Copy Schedule To Dates
          </h2>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground transition-colors"
          >
            <Icon name="X" size={24} />
          </button>
        </div>

        {/* Modal Body */}
        <div className="flex-1 overflow-y-auto p-4 md:p-6 custom-scrollbar">
          {/* Month Navigation */}
          <div className="flex items-center justify-between mb-6">
            <button
              onClick={handlePrevMonth}
              className="p-2 hover:bg-muted rounded-lg transition-colors"
            >
              <Icon name="ChevronLeft" size={20} />
            </button>
            <h3 className="font-heading font-semibold text-lg">{monthName}</h3>
            <button
              onClick={handleNextMonth}
              className="p-2 hover:bg-muted rounded-lg transition-colors"
            >
              <Icon name="ChevronRight" size={20} />
            </button>
          </div>

          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Icon name="Loader" size={32} className="animate-spin text-primary" />
            </div>
          ) : (
            <>
              {/* Week Days Header */}
              <div className="grid grid-cols-7 gap-2 mb-3">
                {weekDays.map(day => (
                  <div key={day} className="text-center text-xs font-caption font-medium text-muted-foreground py-2">
                    {day}
                  </div>
                ))}
              </div>

              {/* Calendar Grid */}
              <div className="grid grid-cols-7 gap-2 mb-6">
                {calendarDays.map((day, index) => {
                  if (!day) {
                    return <div key={`empty-${index}`} />;
                  }

                  const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
                  const isoDate = date.toISOString().split('T')[0];
                  const dayData = availabilityData[isoDate];
                  const dateSlotId = dayData?.dateSlotId || dayData?.id;
                  const isSelected = selectedDateSlotIds.includes(dateSlotId);
                  const isAvailable = dayData?.isAvailable !== false && dateSlotId;

                  return (
                    <button
                      key={day}
                      onClick={() => isAvailable && dateSlotId && handleDateSelect(dateSlotId)}
                      disabled={!isAvailable || !dateSlotId}
                      className={`
                        aspect-square rounded-lg flex items-center justify-center
                        transition-all duration-250 ease-elegant font-caption
                        ${!isAvailable || !dateSlotId ? 'opacity-50 cursor-not-allowed bg-muted' : ''}
                        ${isAvailable && isSelected ? 'bg-primary text-primary-foreground shadow-md' : ''}
                        ${isAvailable && !isSelected ? 'hover:bg-primary/10 cursor-pointer border border-border' : ''}
                      `}
                    >
                      {day}
                    </button>
                  );
                })}
              </div>

              {/* Selection Summary */}
              <div className="bg-muted/50 rounded-lg p-4 mb-6">
                <p className="text-sm text-foreground font-caption">
                  <span className="font-semibold">{selectedDateSlotIds.length}</span> date(s) selected
                </p>
              </div>
            </>
          )}
        </div>

        {/* Modal Footer */}
        <div className="flex items-center justify-end gap-3 p-4 md:p-6 border-t border-border">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={isCopying}
          >
            Cancel
          </Button>
          <Button
            variant="default"
            onClick={handleCopy}
            disabled={selectedDateSlotIds.length === 0 || isCopying || isLoading}
            iconName="Copy"
            iconPosition="left"
          >
            {isCopying ? 'Applying...' : 'Apply Schedule'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CopyScheduleModal;
